import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Star } from "lucide-react";

const EMPTY = { name: "", zone: "", text: "", rating: 5, source: "google", published: true };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, rating: Number(editing.rating) || 5 };
      if (editing.id) {
        await api.put(`/admin/testimonials/${editing.id}`, payload);
        toast.success("Avis mis à jour");
      } else {
        await api.post("/admin/testimonials", payload);
        toast.success("Avis ajouté");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (t) => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, { ...t, published: !t.published });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer l'avis de « ${t.name} » ?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Avis supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const input = "mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal";

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Avis clientes</h1>
          <p className="text-sm text-ink/60 mt-1">Copiez-collez vos avis Google ou WhatsApp — ils s'affichent sur l'accueil.</p>
        </div>
        <button data-testid="admin-testimonial-new" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          <Plus size={16} /> Nouvel avis
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-white rounded-3xl border border-line p-4 md:p-5 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-teal-dark">{t.name}</p>
                {t.zone && <span className="text-xs text-mauve">• {t.zone}</span>}
                <span className="flex gap-0.5 ml-1">
                  {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={12} className={i <= t.rating ? "fill-teal text-teal" : "text-line"} />)}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-mauve ml-1">{t.source === "google" ? "Google" : "WhatsApp"}</span>
              </div>
              <p className="mt-1.5 text-sm text-ink/75 line-clamp-2">{t.text}</p>
            </div>
            <button
              data-testid={`admin-testimonial-toggle-${t.id}`}
              onClick={() => togglePublished(t)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${t.published ? "bg-mint/30 text-teal-dark" : "bg-line text-ink/60"}`}
            >
              {t.published ? "Publié" : "Masqué"}
            </button>
            <button data-testid={`admin-testimonial-edit-${t.id}`} onClick={() => setEditing({ ...EMPTY, ...t })} className="p-2 text-teal" aria-label="Modifier"><Pencil size={16} /></button>
            <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => remove(t)} className="p-2 text-[#b04a4a]" aria-label="Supprimer"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && (
          <p data-testid="admin-testimonials-empty" className="bg-white rounded-3xl border border-line p-10 text-center text-ink/60">
            Aucun avis pour l'instant — ajoutez vos premiers avis Google ou WhatsApp.
          </p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-teal-deep/40 backdrop-blur-sm p-4" data-testid="admin-testimonial-modal">
          <form onSubmit={save} data-lenis-prevent className="w-full max-w-xl max-h-[90vh] overflow-y-auto overscroll-contain bg-white rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-teal-dark">{editing.id ? "Modifier l'avis" : "Nouvel avis"}</h2>
              <button type="button" data-testid="admin-testimonial-modal-close" onClick={() => setEditing(null)} aria-label="Fermer"><X size={22} className="text-mauve" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-ink">
                  Prénom de la cliente *
                  <input required data-testid="admin-testimonial-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Aïssatou D." className={input} />
                </label>
                <label className="block text-sm font-medium text-ink">
                  Quartier (optionnel)
                  <input data-testid="admin-testimonial-zone" value={editing.zone} onChange={(e) => setEditing({ ...editing, zone: e.target.value })} placeholder="Almadies, Dakar" className={input} />
                </label>
              </div>
              <label className="block text-sm font-medium text-ink">
                Avis *
                <textarea required rows={5} data-testid="admin-testimonial-text" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} placeholder="Collez ici le texte de l'avis…" className={input} />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-ink">
                  Note
                  <select data-testid="admin-testimonial-rating" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} className={input}>
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium text-ink">
                  Source
                  <select data-testid="admin-testimonial-source" value={editing.source} onChange={(e) => setEditing({ ...editing, source: e.target.value })} className={input}>
                    <option value="google">Avis Google</option>
                    <option value="whatsapp">Message WhatsApp</option>
                  </select>
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" data-testid="admin-testimonial-published" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-teal" />
                Afficher sur le site
              </label>
            </div>
            <button type="submit" data-testid="admin-testimonial-save" disabled={saving} className="mt-8 w-full rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
